import { useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";
import { ChevronRight, PenLine, X, FileSignature } from "lucide-react";

// Reusable "I've read it → Sign here" bottom-sheet sign panel.
// mode: "proposal" | "loe"; requireOtp only used for the proposal step.
export default function SignPanel({ token, mode, requireOtp, signatory, statements = [], packageId, alignment, onDone }) {
  const [open, setOpen] = useState(false);
  const isLoe = mode === "loe";
  const [phase, setPhase] = useState(requireOtp && !isLoe ? "otp_email" : "details");
  const [email, setEmail] = useState(signatory?.email || "");
  const [code, setCode] = useState("");
  const [sig, setSig] = useState({
    name: signatory?.name || "", job_title: signatory?.job_title || "",
    company: signatory?.company || "", signature_text: "",
  });
  const [confirms, setConfirms] = useState({ authority: false, reviewed: false, terms: false });
  const [loeConfirm, setLoeConfirm] = useState(false);
  const [alignChoice, setAlignChoice] = useState("");
  const [busy, setBusy] = useState(false);

  const label = isLoe ? "engagement letter" : "proposal";

  const requestOtp = async () => {
    if (!email) return toast.error("Enter your email");
    setBusy(true);
    try { await api.post(`/public/proposals/${token}/request-otp`, { email }); setPhase("otp_code"); toast.success("Verification code sent to your email"); }
    catch { toast.error("Could not send code"); }
    finally { setBusy(false); }
  };
  const verifyOtp = async () => {
    setBusy(true);
    try {
      await api.post(`/public/proposals/${token}/verify-otp`, { email, code });
      setPhase("details");
      try { localStorage.setItem(`cass_sign_${token}`, JSON.stringify({ email, at: Date.now() })); } catch { /* ignore */ }
      toast.success("Email verified");
    } catch { toast.error("Invalid or expired code"); }
    finally { setBusy(false); }
  };

  const submitProposal = async () => {
    if (!sig.name || !sig.job_title) return toast.error("Name and job title required");
    if (!confirms.authority || !confirms.reviewed || !confirms.terms) return toast.error("Please tick all confirmations");
    if (alignment?.required && !alignChoice) return toast.error("Please choose how you'd like to pay the alignment fee");
    if (!sig.signature_text) return toast.error("Type your signature");
    setBusy(true);
    try {
      const res = await api.post(`/public/proposals/${token}/accept`, {
        email, name: sig.name, job_title: sig.job_title, company: sig.company,
        signature_text: sig.signature_text, method: "typed",
        confirm_authority: confirms.authority, confirm_reviewed: confirms.reviewed, confirm_terms: confirms.terms,
        package_id: packageId || undefined,
        alignment_choice: alignChoice || undefined,
      });
      try { localStorage.removeItem(`cass_sign_${token}`); } catch { /* ignore */ }
      if (res.data?.status === "partial") {
        toast.success(`Signed — waiting for the other signatories (${res.data.signed} of ${res.data.total} done)`);
      } else {
        toast.success("Accepted and signed");
      }
      onDone?.();
    } catch (e) {
      if (e.response?.status === 403) {
        setPhase("otp_email");
        try { localStorage.removeItem(`cass_sign_${token}`); } catch { /* ignore */ }
        toast.error("Your email verification expired — please verify again.");
      } else toast.error(e.response?.data?.detail || "Could not accept");
    } finally { setBusy(false); }
  };

  const submitLoe = async () => {
    if (!sig.name || !sig.job_title) return toast.error("Name and job title required");
    if (!loeConfirm) return toast.error("Please confirm acceptance of the engagement terms");
    if (!sig.signature_text) return toast.error("Type your signature");
    setBusy(true);
    try {
      await api.post(`/public/proposals/${token}/loe/sign`, {
        email, name: sig.name, job_title: sig.job_title, company: sig.company,
        signature_text: sig.signature_text, method: "typed", confirm_all: loeConfirm,
      });
      toast.success("Letter of Engagement signed");
      onDone?.();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not sign"); }
    finally { setBusy(false); }
  };

  // Collapsed sticky bar
  if (!open) {
    return (
      <div className="fixed bottom-0 inset-x-0 z-40 bg-white/95 backdrop-blur-xl border-t border-border shadow-[0_-4px_20px_rgba(0,0,0,0.08)]">
        <div className="max-w-[840px] mx-auto flex items-center justify-between gap-4 px-5 py-4">
          <div className="text-sm text-cass-slate">
            <span className="font-semibold text-cass-navy">Ready to continue?</span>
            <span className="hidden sm:inline"> Please review the {label} above, then sign.</span>
          </div>
          <Button className="bg-cass-teal text-white rounded-full px-5 h-11 shrink-0" onClick={() => setOpen(true)} data-testid={`open-sign-${mode}`}>
            {isLoe ? <FileSignature className="w-4 h-4 mr-2" /> : <PenLine className="w-4 h-4 mr-2" />}
            I've read it — Sign here
          </Button>
        </div>
      </div>
    );
  }

  // Expanded bottom sheet
  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center">
      <div className="absolute inset-0 bg-cass-navy/40 backdrop-blur-sm" onClick={() => setOpen(false)} />
      <div className="relative w-full max-w-lg bg-white rounded-t-3xl sm:rounded-3xl max-h-[88vh] overflow-y-auto shadow-2xl" data-testid={`sign-sheet-${mode}`}>
        <div className="sticky top-0 bg-white flex items-center justify-between px-6 py-4 border-b border-border">
          <div className="font-display text-xl text-cass-navy">Sign your {label}</div>
          <button onClick={() => setOpen(false)} className="text-cass-slate hover:text-cass-navy" data-testid={`close-sign-${mode}`}><X className="w-5 h-5" /></button>
        </div>
        <div className="p-6 space-y-4 text-cass-navy">
          {phase === "otp_email" && (
            <div className="space-y-4">
              <p className="text-cass-slate text-sm">To sign, we first verify your email with a one-time code.</p>
              <div><Label>Your email</Label><Input value={email} onChange={(e) => setEmail(e.target.value)} data-testid="sign-email" /></div>
              <Button className="bg-cass-teal text-white rounded-full w-full" disabled={busy} onClick={requestOtp} data-testid="request-otp-btn">Send verification code<ChevronRight className="w-4 h-4 ml-1" /></Button>
            </div>
          )}
          {phase === "otp_code" && (
            <div className="space-y-4">
              <p className="text-cass-slate text-sm">Enter the 6-digit code we emailed to {email}.</p>
              <Input value={code} onChange={(e) => setCode(e.target.value)} maxLength={6} className="text-center text-2xl tracking-[0.5em]" data-testid="otp-input" />
              <Button className="bg-cass-teal text-white rounded-full w-full" disabled={busy} onClick={verifyOtp} data-testid="verify-otp-btn">Verify email</Button>
              <button className="text-xs text-cass-slate underline w-full" onClick={requestOtp}>Resend code</button>
            </div>
          )}
          {phase === "details" && (
            <div className="space-y-4">
              {(sig.name || sig.job_title) && (
                <div className="text-sm bg-cass-bone rounded-lg px-3 py-2 text-cass-slate" data-testid={`signing-as-${mode}`}>
                  Signing as <span className="font-semibold text-cass-navy">{sig.name || "—"}</span>{sig.job_title ? `, ${sig.job_title}` : ""}
                </div>
              )}
              <div className="grid grid-cols-2 gap-3">
                <div><Label>Full name</Label><Input value={sig.name} onChange={(e) => setSig({ ...sig, name: e.target.value })} data-testid="sign-name" /></div>
                <div><Label>Job title</Label><Input value={sig.job_title} onChange={(e) => setSig({ ...sig, job_title: e.target.value })} data-testid="sign-title" /></div>
              </div>
              <div><Label>Company represented</Label><Input value={sig.company} onChange={(e) => setSig({ ...sig, company: e.target.value })} data-testid="sign-company" /></div>

              {isLoe ? (
                <>
                  {statements.length > 0 && (
                    <div className="text-sm space-y-2 bg-cass-bone rounded-lg p-3">
                      {statements.map((s, i) => <div key={i} className="text-cass-slate flex gap-2"><span className="text-cass-teal">•</span>{s}</div>)}
                    </div>
                  )}
                  <label className="flex items-start gap-2 cursor-pointer text-sm">
                    <Checkbox checked={loeConfirm} onCheckedChange={(v) => setLoeConfirm(!!v)} data-testid="loe-confirm" className="mt-0.5" />
                    <span className="text-cass-slate">I acknowledge receipt and acceptance of the letter of engagement, schedules of services, privacy notice and standard terms and conditions, and confirm the statements above.</span>
                  </label>
                </>
              ) : (
                <div className="space-y-2 text-sm">
                  {[["authority", "I confirm I have authority to accept on behalf of the company."],
                    ["reviewed", "I have reviewed the proposal and services."],
                    ["terms", "I accept the engagement terms and service schedules."]].map(([k, txt]) => (
                    <label key={k} className="flex items-start gap-2 cursor-pointer">
                      <Checkbox checked={confirms[k]} onCheckedChange={(v) => setConfirms({ ...confirms, [k]: !!v })} data-testid={`confirm-${k}`} className="mt-0.5" />
                      <span className="text-cass-slate">{txt}</span>
                    </label>
                  ))}
                </div>
              )}

              {!isLoe && alignment?.required && (
                <div className="space-y-2 text-sm bg-cass-bone rounded-lg p-3" data-testid="alignment-choice">
                  <div className="font-semibold text-cass-navy">Alignment fee — £{Number(alignment.gross || 0).toFixed(2)} inc VAT</div>
                  <p className="text-cass-slate text-xs">Please choose how you'd like to pay this{alignment.period ? ` (${alignment.period})` : ""}:</p>
                  <label className="flex items-start gap-2 cursor-pointer">
                    <input type="radio" name="align-choice" className="mt-1" checked={alignChoice === "one_off"} onChange={() => setAlignChoice("one_off")} data-testid="align-full" />
                    <span className="text-cass-slate">Pay in full — <span className="font-semibold text-cass-navy">£{Number(alignment.gross || 0).toFixed(2)} inc VAT</span></span>
                  </label>
                  <label className="flex items-start gap-2 cursor-pointer">
                    <input type="radio" name="align-choice" className="mt-1" checked={alignChoice === "year_end"} onChange={() => setAlignChoice("year_end")} data-testid="align-spread" />
                    <span className="text-cass-slate">Spread to year-end — <span className="font-semibold text-cass-navy">{alignment.spread?.instalments} monthly payments of £{(Number(alignment.spread?.monthly || 0) * 1.2).toFixed(2)} inc VAT</span></span>
                  </label>
                </div>
              )}

              <div>
                <Label>Signature</Label>
                <Input placeholder="Type your full name as signature" className="font-display text-xl" value={sig.signature_text} onChange={(e) => setSig({ ...sig, signature_text: e.target.value })} data-testid={isLoe ? "loe-sig-input" : "sig-typed-input"} />
              </div>

              <Button className="bg-cass-teal text-white rounded-full w-full h-11" disabled={busy} onClick={isLoe ? submitLoe : submitProposal} data-testid={isLoe ? "loe-sign-btn" : "submit-accept-btn"}>
                <PenLine className="w-4 h-4 mr-2" />{isLoe ? "Sign engagement letter" : "Accept & sign"}
              </Button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
